import { useState, useCallback, useReducer } from 'react'
import type { Website } from '@app/types'
import { SortDirection } from '@app/components/general/dashboard-website-list'

export function arrayAverage(arr: any[] = []): number {
  let sum = 0

  for (const i in arr) {
    sum += arr[i]
  }

  return sum / (arr.length || 1)
}

// sort a website by order
export type SortMap = Map<
  string,
  { sortDirection: SortDirection; sortKey: string }
>

/*
 * This hook returns all the queries, mutations, and subscriptions between your Website with the graphs,
 * Pages, and Issues with pagination.
 * @example const {data} = useWebsiteData(); // returns websites
 * @example const {data} = useWebsiteData('', 'www.somepage.com', null, false); bind to a website for mutations.
 *
 */
export const useWebsiteDataMock = () => {
  const [sortDirections, setSortDirection] = useState<SortMap>(new Map())
  const [_, forceUpdate] = useReducer((x) => x + 1, 0) // top level force update state
  const [feedOpen, setIssueFeedContent] = useState<boolean>(false)
  const [activeCrawls, setActiveCrawl] = useState<
    { [key: string]: boolean } | Record<string, any>
  >({})

  // start of main queries for pages. Root gets all
  const [getWebsites, { loading, refetch, error }] = [
    () => {},
    { loading: false, refetch: () => {}, error: undefined },
  ]

  // Get Scripts Pages
  const { loading: actionsDataLoading } = {
    loading: false,
  }

  const [removeWebsite, { loading: removeLoading }] = [
    () => {},
    { loading: false },
  ]

  const [removePage, { loading: removePageLoading }] = [
    () => {},
    { loading: false },
  ]

  const [updateWebsite] = [() => {}]

  // SCOPE WEBSITE DATA PER ROUTE (ALL, ISSUES, PAGES)
  const websites: Website[] = []

  // full domain crawl for reports
  const crawlWebsite = async () => {}

  const singlePageScan = async () => {}

  // dashboard page pagination
  const onLoadMoreWebsites = async () => {}

  // actions page pagination
  const onLoadMoreActions = async () => {}

  // toggle the live feed menu
  const setFeed = (open: boolean) => setIssueFeedContent(open)

  // add a website to monitor
  const addWebsite = async () => {}

  const removeActiveCrawl = useCallback(
    (domain: string) => {
      setActiveCrawl((v) => ({
        ...v,
        [domain]: false,
      }))
    },
    [setActiveCrawl]
  )

  const onSortEvent = useCallback(
    (domain: string, direction: SortDirection, sortKey?: string) => {
      setSortDirection((m) => {
        let map = new Map(m)
        map.set(domain, { sortDirection: direction, sortKey: sortKey || '' })
        return map
      })
    },
    [setSortDirection]
  )

  return {
    subscriptionData: {
      issueSubData: null,
    },
    data: websites as any, // TODO: rename to websites.
    actionsData: null as any, // [scopred] collection of actions
    actionsDataLoading, // [scoped] issues loading
    loading,
    onSortEvent,
    error, // general mutation error
    mutatationLoading: removeLoading || removePageLoading,
    // other state
    activeCrawls,
    // mobile feed open
    feedOpen,
    removeWebsite: removeWebsite as any,
    addWebsite,
    removePage,
    refetch: refetch as any,
    crawlWebsite,
    singlePageScan, // single page web scan
    updateWebsite,
    setIssueFeedContent: setFeed,
    // pagination
    onLoadMoreWebsites,
    onLoadMoreActions,
    removeActiveCrawl,
    // forceupdate
    forceUpdate,
    // lazy queries
    getWebsites,
    sortDirections,
  }
}
